import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { AddCircleOutline, MoreVert } from "@mui/icons-material";
import { useGetApi } from "../hooks/useGetApi";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import { getStoreApi, deleteStoreByIdApi } from "../services/store.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import AddStockModal from "../components/store/modals/add-stock-modal";
import dayjs from "dayjs";
import StoreStockViewModal from "../components/store/modals/store-stock-view-modal";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { hasPermission } from "../utils/permissionHelper";

const headLabel = [
  {
    id: 1,
    label: "SNO",
    value: "sno",
  },
  {
    id: 2,
    label: "Item Code",
    value: "itemCode",
  },
  {
    id: 3,
    label: "Batch Code",
    value: "batchNo",
  },
  {
    id: 4,
    label: "Item Name",
    value: "itemName",
  },
  {
    id: 5,
    label: "Item Type",
    value: "itemType",
  },
  {
    id: 6,
    label: "Available Quantity",
    value: "quantity",
  },
  {
    id: 7,
    label: "Expiry Date",
    value: "expiryDate",
  },
  {
    id: 8,
    label: "Supplier Name",
    value: "supplier",
  },
  {
    id: 9,
    label: "",
    value: "",
    align: "right",
  },
];

const popoverOptions = ["View Details", "Add Stock", "Delete"];

const buttons = [
  { label: "Expiring", width: "150px" },
  { label: "Depleting", width: "150px" },
];

const Store = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("itemCode");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_LIMIT });
  const [sortModel, setSortModel] = useState([]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorModalEl, setAnchorModalEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeButton, setActiveButton] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [dependencies, setDependencies] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handlePaginationModelChange = (newModel) => setPaginationModel(newModel);
  const handleSortModelChange = (newSortModel) => setSortModel(newSortModel);

  const {
    dataList: storeList,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getStoreApi,
    params: {
      limit: 10000,
      page: 1,
      search,
      searchBy,
      filter: activeButton?.toLowerCase(),
    },
    dependencies: [search, searchBy, activeButton],
    debounceDelay: 500,
    dataKey: "stocks",
    countKey: "total",
  });

  const rows = storeList?.map((row, index) => ({
    id: row._id ?? `store-${index}`,
    _id: row._id,
    serialNo: row?.serialNo ?? "—",
    itemCode: row?.itemDetails?.itemCode ?? "—",
    batchNo: row?.batchNo ?? "—",
    itemName: row?.itemDetails?.itemName ?? "—",
    itemType: row?.itemDetails?.itemType ?? "—",
    quantity: row?.quantity ?? "—",
    expiryDate: row?.expiryDate,
    supplier: row?.supplier ?? "—",
    itemDetails: row?.itemDetails,
  })) ?? [];

  const getColumns = useCallback(() => {
    const baseColumns = [
      { field: "serialNo", headerName: "SNO", flex: isMobile ? 0 : 0.5, width: isMobile ? 60 : undefined, minWidth: 50, headerAlign: "center", align: "center", sortable: true },
      { field: "itemCode", headerName: "Item Code", flex: isMobile ? 0 : 0.8, width: isMobile ? 90 : undefined, minWidth: 80, headerAlign: "center", align: "center", sortable: true },
      { field: "batchNo", headerName: "Batch Code", flex: isMobile ? 0 : 0.8, width: isMobile ? 90 : undefined, minWidth: 80, headerAlign: "center", align: "center", sortable: true },
      { field: "itemName", headerName: "Item Name", flex: isMobile ? 0 : 1.2, width: isMobile ? 120 : undefined, minWidth: 100, headerAlign: "center", align: "center", sortable: true },
      { field: "itemType", headerName: "Item Type", flex: isTablet ? 0 : 0.7, width: isTablet ? 90 : undefined, minWidth: 80, headerAlign: "center", align: "center", sortable: true },
      { field: "quantity", headerName: "Available Quantity", flex: isTablet ? 0 : 0.7, width: isTablet ? 90 : undefined, minWidth: 80, headerAlign: "center", align: "center", sortable: true },
      { field: "expiryDate", headerName: "Expiry Date", flex: isTablet ? 0 : 0.8, width: isTablet ? 100 : undefined, minWidth: 90, headerAlign: "center", align: "center", sortable: true, renderCell: (params) => (<Box component="span" sx={{ color: "red" }}>{params.value ? dayjs(params.value).format("YY/MM/DD") : "—"}</Box>) },
      { field: "supplier", headerName: "Supplier Name", flex: isTablet ? 0 : 0.9, width: isTablet ? 110 : undefined, minWidth: 100, headerAlign: "center", align: "center", sortable: true },
      { field: "actions", headerName: "Actions", flex: 0, width: 70, minWidth: 60, headerAlign: "center", align: "center", sortable: false, renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton size={isMobile ? "small" : "medium"} onClick={(e) => { e.stopPropagation(); handleClick(e, params.row); }}><MoreVert sx={{ cursor: "pointer" }} /></IconButton>
          {activeButton === "Depleting" && (<Button variant="outlined" sx={{ color: "error.light", borderColor: "error.light", borderRadius: "20px", textTransform: "none" }}>Add Stock</Button>)}
        </Box>
      ) },
    ];
    return baseColumns;
  }, [isMobile, isTablet, activeButton]);

  const handleRightClick = (event, row) => {
    event.preventDefault(); // Prevent the default context menu
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewDetailsModalClick = (event, buttonOption) => {
    if (buttonOption === "View Details") {
      setAnchorModalEl(event.currentTarget);
    } else if (buttonOption === "Add Stock") {
      navigate(selectedRow?._id);
    } else if (buttonOption === "Delete") {
      handleDeleteClick();
    }
    handleClose(); // Close the menu
  };

  const handleDeleteClick = () => {
    setItemToDelete(selectedRow);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setLoading(true);
    try {
      const response = await deleteStoreByIdApi(itemToDelete._id);
      if (response.code === 200) {
        toast.success("Stock deleted successfully!");
        refetch(); // Refresh the list
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        setDependencies(null);
      } else if (response?.code === 400 && response?.data) {
        // Handle dependency error
        setDependencies(response.data);
      } else {
        toast.error(response.message || "Failed to delete stock");
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        setDependencies(null);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred while deleting the stock");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDependencies(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setDependencies(null);
  };

  const handleViewDetailsModalClose = () => {
    setAnchorModalEl(null);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Search and Buttons */}
          <Box
            sx={{
              marginTop: "20px",
              marginBottom: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder="Search by Item Code"
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>

            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
              onClick={handleModalOpen}
            >
              <AddCircleOutline />
              <Typography sx={{ color: "white" }}>Add New Stock</Typography>
            </Button>
          </Box>
          {/* Filter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "40px",
            }}
          >
            <FormControl size="small" sx={{ minWidth: "100px" }}>
              <InputLabel id="filter-select-label">Filter</InputLabel>
              <Select
                labelId="filter-select-label"
                label="Filter"
                defaultValue="All"
              >
                <MenuItem value="All">All</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {buttons?.map((button) => (
                <Button
                  key={button?.label}
                  variant="outlined"
                  onClick={() => setActiveButton(button?.label)}
                  sx={{
                    border: `2px solid ${
                      activeButton === button?.label
                        ? theme.palette.primary.dark
                        : "#92A6CC"
                    }`,
                    color:
                      activeButton === button?.label
                        ? theme.palette.primary.dark
                        : "text.secondary",
                    bgcolor:
                      activeButton === button?.label ? "#AED5F3" : "#F0F7FC",
                    fontWeight: activeButton === button?.label ? 600 : 400,
                    width: button?.width || "100px",
                    textTransform: "none",
                  }}
                >
                  {button?.label || ""}
                </Button>
              ))}
            </Box>
          </Box>
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <>
              <Box>
                <Box sx={{ height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" }, width: "100%", minHeight: "300px", maxWidth: "100%", overflow: "hidden" }}>
                  <DataGrid
                    rows={rows}
                    columns={getColumns()}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                    paginationMode="client"
                    sortingMode="client"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    disableRowSelectionOnClick
                    loading={isLoading}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-root": { border: "none", width: "100%" },
                      "& .MuiDataGrid-main": { width: "100%" },
                      "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0", padding: isMobile ? "4px" : "8px", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" },
                      "& .MuiDataGrid-columnHeader": { backgroundColor: "#f5f5f5", fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-row": { "&:hover": { backgroundColor: "#f5f5f5", cursor: "pointer" }, "&:nth-of-type(odd)": { backgroundColor: "#fafafa" }, "&:nth-of-type(even)": { backgroundColor: "white" } },
                      "& .MuiDataGrid-footerContainer": { borderTop: "2px solid #e0e0e0", backgroundColor: "#f9f9f9" },
                      "& .MuiDataGrid-virtualScroller": { minHeight: "200px" },
                      [theme.breakpoints.down("sm")]: { "& .MuiDataGrid-columnHeader": { fontSize: "11px", padding: "4px" }, "& .MuiDataGrid-cell": { fontSize: "11px", padding: "2px 4px" } },
                    }}
                    componentsProps={{ row: { onContextMenu: (event) => { event.preventDefault(); const rowId = event.currentTarget.getAttribute("data-id"); const rowData = rows.find((r) => String(r.id) === rowId); if (rowData) handleRightClick(event, rowData); } } }}
                    components={{ NoRowsOverlay: () => (<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}><Typography variant="body2" color="text.secondary">No store items found</Typography></Box>) }}
                  />
                </Box>
              </Box>
            </>
          )}
          {/* create and upload modal */}
          <AddStockModal open={modalOpen} onClose={handleModalClose} />

          {/* view details modal */}
          <StoreStockViewModal
            open={Boolean(anchorModalEl)}
            onClose={handleViewDetailsModalClose}
            selectedRow={selectedRow}
          />

          {/* Pop-up Menu */}
          <Menu
            id={Boolean(anchorEl) ? "row-popover" : undefined}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
            anchorPosition={
              anchorPosition
                ? { top: anchorPosition.top, left: anchorPosition.left }
                : undefined
            }
            anchorOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: "8px",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                  maxHeight: "300px",
                },
              },
            }}
          >
            {popoverOptions
              ?.filter(option => {
                if (option === "Delete") {
                  return hasPermission(user, "Store.Delete");
                }
                return true;
              })
              ?.map((option, index) => (
                <MenuItem key={index} onClick={(e) => handleViewDetailsModalClick(e, option)}>
                  {option || ""}
                </MenuItem>
              ))}
          </Menu>

          {/* Delete Confirmation Dialog */}
          <DeleteConfirmationDialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete Stock"
            itemName={itemToDelete ? `stock item "${itemToDelete.itemDetails?.itemName || itemToDelete.batchNo}"` : "this stock item"}
            dependencies={dependencies}
            isLoading={loading}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default Store;
